import React from 'react'
import { useDispatch } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { FREQUENCY } from '@classy/campaign-page-blocks'
import paypalApi from 'redux/services/paypal'
import { setPaymentMethod, setPaymentType } from 'redux/features/paymentDetails'
import { PaymentMethod } from 'models/ChargeIntent'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import useFlow from 'modules/flow/state/use-flow'
import { getClassySessionId } from 'modules/utils/getClassySessionId'

interface PaypalButtonWrapperProps {
  fee: number
  fundingSource: 'paypal' | 'venmo'
}

// Custom component to wrap the PayPalButtons and handle currency changes
const PaypalButtonWrapper = ({ fundingSource }: PaypalButtonWrapperProps) => {
  const order = useOrder()
  const config = useConfig()
  const dispatch = useDispatch<TypedDispatch<RootState>>()
  const appDispatch = useDispatch<TypedDispatch<RootState>>()

  /**
   * If dcfNudge.enabled = true, donor has already seen a dcf checkbox
   * and dcf.value.totalAmount will show the amount with or without DCF
   *
   * Otherwise, if dcfNudge is disabled but dcf is checked by default,
   * dcf.value.totalAmount will include fees, which donor has not yet seen.
   * Therefore, in this case we should always use order.amount.value
   *
   * This also means that if dcfNudge is not enabled,
   * and a donor makes a DCF decision AFTER payment selection
   * Then decides to backtrack to payment selection, we will still use the amount without DCF
   * This is fine, bc they will have the option to check dcf on the next step again anyway
   */
  const amount = config.dcfNudge.enabled
    ? `${order.dcf.value.totalAmount}`
    : `${order.amount.value}`

  const campaignId = config.campaignId
  const currency = order.currency.value
  const frequency = order.frequency.value

  const isRecurring = frequency !== FREQUENCY.ONE_TIME

  const paypalOptions = usePaypalOptions(fundingSource)

  const setPaypalOrderId = useSetPaypalOrderId(fundingSource)

  if (paypalOptions.options.enableFunding != fundingSource) {
    // This forces a full re-render if the user toggles between paypal & venmo on the
    // payment selection screen. (The forceReRender param below doesn't seem to work for that)
    return <div></div>
  }

  if (paypalOptions.isLoading)
    return <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />

  return (
    <span style={{ zIndex: 1 }}>
      <PayPalButtons
        data-id="payment-method-button-PayPal"
        disabled={false}
        onError={console.log}
        fundingSource={fundingSource}
        forceReRender={[amount, currency, frequency]}
        style={{ layout: 'vertical', shape: 'pill', height: 55 }}
        /**
         * For onetime order
         */
        createOrder={
          !isRecurring
            ? async () => {
                const body = {
                  campaignId,
                  amount,
                  currency,
                  paymentMethod:
                    fundingSource == 'venmo' ? PaymentMethod.Venmo : PaymentMethod.PayPal_Commerce,
                }

                const order = await appDispatch(
                  paypalApi.endpoints.createOrder.initiate(body),
                ).unwrap()

                return order.orderId
              }
            : undefined
        }
        /**
         * For recurring order
         */
        createBillingAgreement={
          isRecurring
            ? async () => {
                const response = await dispatch(
                  paypalApi.endpoints.getPaypalBillingAgreement.initiate({
                    campaignId,
                    currency,
                  }),
                ).unwrap()

                return response.billingAgreementToken
              }
            : undefined
        }
        onApprove={async (data) => {
          const orderId = isRecurring ? data.billingToken : data.orderID

          if (!orderId) return

          setPaypalOrderId(orderId)

          if (window.newrelic) {
            window.newrelic.addPageAction('payment_method_selected', {
              'classy-session-id': getClassySessionId(),
              paymentMethod: PaymentMethod.PayPal_Commerce,
            })
          }
        }}
      />
    </span>
  )
}

export default PaypalButtonWrapper

const useSetPaypalOrderId = (fundingSource: PaypalButtonWrapperProps['fundingSource']) => {
  const flow = useFlow()
  const order = useOrder()
  const dispatch = useDispatch<TypedDispatch<RootState>>()

  const isRecurring = order.frequency.value !== FREQUENCY.ONE_TIME

  const [orderId, setOrderId] = React.useState('')

  const { data: oneTimeOrder } = paypalApi.useGetOrderQuery(
    { orderId },
    { skip: !orderId || isRecurring },
  )

  const { data: recurringOrder } = paypalApi.useGetBillingAgreementTokenDetailsQuery(
    { billingAgreementTokenId: orderId },
    { skip: !orderId || !isRecurring },
  )

  const paypalOrder = isRecurring ? recurringOrder : oneTimeOrder

  React.useEffect(() => {
    if (!paypalOrder) return

    order.address.setValue({
      city: paypalOrder.city,
      country: paypalOrder.country_code,
      line1: paypalOrder.address_line_1,
      zipCode: paypalOrder.postal_code,
      state: paypalOrder.state,
    })

    const isVenmo = fundingSource == 'venmo'
    dispatch(setPaymentMethod(isRecurring ? { agreementId: orderId } : { orderId }))

    dispatch(setPaymentType(isVenmo ? PaymentMethod.Venmo : PaymentMethod.PayPal_Commerce))

    flow.nextStep && flow.goToStep(flow.nextStep.name)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalOrder])

  return setOrderId
}

const usePaypalOptions = (fundingSource: PaypalButtonWrapperProps['fundingSource']) => {
  const order = useOrder()

  const [{ options, isPending, isResolved }, paypalDispatch] = usePayPalScriptReducer()

  React.useEffect(() => {
    if (options.enableFunding === fundingSource && (isPending || isResolved)) return

    paypalDispatch({
      type: 'resetOptions',
      value: { ...options, enableFunding: fundingSource, currency: order.currency.value },
    })
  }, [options, isPending, isResolved, fundingSource, order.currency.value, paypalDispatch])

  return { options, isLoading: isPending }
}
